import { z } from 'zod';
import { isPermission, type Permission } from './permissions.ts';

/** Accepts messy human input ("  Ravi@Gmail.com ") and normalises it. */
export const emailSchema = z
  .string({ error: 'Please enter an email address.' })
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: 'That doesn’t look like a valid email address.' }).max(254));

/**
 * Stores every number in one format (+<country><number>) so search, WhatsApp links and calling work.
 * Indian numbers can be typed any common way: 98765 43210, 098765 43210, 91 98765 43210, +91-98765-43210,
 * and landlines with their STD code: 040 1234 5678.
 */
export function normalizePhone(input: string): string {
  const v = input.trim().replace(/[\s\-().]/g, '');
  if (/^\d{10}$/.test(v)) return `+91${v}`;
  if (/^0\d{10}$/.test(v)) return `+91${v.slice(1)}`;
  if (/^91\d{10}$/.test(v)) return `+${v}`;
  if (/^00\d{8,15}$/.test(v)) return `+${v.slice(2)}`;
  return v;
}

/** "+919876543210" → "+91 98765 43210"; other countries are shown as stored. */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const m = /^\+91(\d{5})(\d{5})$/.exec(phone);
  return m ? `+91 ${m[1]} ${m[2]}` : phone;
}

export const phoneSchema = z
  .string()
  .transform(normalizePhone)
  .pipe(z.string().regex(/^\+\d{8,15}$/, { error: 'Please enter a valid phone number, e.g. 98765 43210.' }));

export const passwordSchema = z
  .string({ error: 'Please enter a password.' })
  .min(8, { error: 'Password must be at least 8 characters.' })
  .max(128, { error: 'Password is too long.' })
  .regex(/[A-Za-z]/, { error: 'Password must contain at least one letter.' })
  .regex(/\d/, { error: 'Password must contain at least one number.' });

const nameSchema = z.string().trim().min(2, { error: 'Please enter a name (at least 2 letters).' }).max(100);
const optionalText = (max: number) => z.string().trim().max(max).optional().or(z.literal('').transform(() => undefined));

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, { error: 'Please enter your password.' }).max(128),
  /** "Keep me signed in" (30 days). Without it, the login ends when the browser closes. */
  remember: z.boolean().optional(),
  /** Phone app: proof this phone was trusted for two-step sign-in (browsers send a cookie). */
  trustToken: z.string().max(200).optional(),
});

export const changePasswordSchema = z
  .object({ currentPassword: z.string().min(1, { error: 'Please enter your current password.' }), newPassword: passwordSchema })
  .refine((v) => v.currentPassword !== v.newPassword, {
    error: 'Your new password must be different from the current one.',
    path: ['newPassword'],
  });

export const createUserSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema.optional().or(z.literal('').transform(() => undefined)),
  roleId: z.uuid({ error: 'Please choose a role.' }),
  reportsTo: z.uuid().nullable().optional(),
  designation: optionalText(100),
});
export const updateUserSchema = createUserSchema.partial();

export const roleSchema = z.object({
  name: nameSchema,
  description: optionalText(200),
  permissions: z
    .array(z.string())
    .max(200)
    .transform((list) => [...new Set(list)].filter(isPermission) as Permission[]),
});

export const companyProfileSchema = z.object({
  name: nameSchema,
  phone: phoneSchema.optional().or(z.literal('').transform(() => undefined)),
  email: emailSchema.optional().or(z.literal('').transform(() => undefined)),
  address: optionalText(500),
  gstin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, { error: 'GSTIN should look like 22AAAAA0000A1Z5.' })
    .optional()
    .or(z.literal('').transform(() => undefined)),
  primaryColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, { error: 'Pick a colour.' })
    .optional(),
});

/** Super admin: create a company together with its first admin. */
export const createTenantSchema = z.object({
  companyName: nameSchema,
  adminName: nameSchema,
  adminEmail: emailSchema,
  adminPhone: phoneSchema.optional().or(z.literal('').transform(() => undefined)),
  planId: z.uuid({ error: 'Please choose a plan.' }),
  trialDays: z.coerce.number().int().min(0).max(90).default(14),
  /** Industry starter pack to set the company up with (optional). */
  industry: z.string().max(40).optional().or(z.literal('').transform(() => undefined)),
});

export const planSchema = z.object({
  name: nameSchema,
  priceMonthly: z.coerce.number().int().min(0).max(10_000_000),
  priceYearly: z.coerce.number().int().min(0).max(100_000_000),
  maxUsers: z.coerce.number().int().min(1).max(10_000),
  isActive: z.boolean().default(true),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type RoleInput = z.infer<typeof roleSchema>;
export type CompanyProfileInput = z.infer<typeof companyProfileSchema>;
export type CreateTenantInput = z.infer<typeof createTenantSchema>;
export type PlanInput = z.infer<typeof planSchema>;
